import { RefObject } from "react";
import loadsh_isEqual from "lodash.isequal";
import { CurrencyCode } from "./util.types";

export const isDevlopment = process.env.NODE_ENV === "development";
export function isJsonString(str: string) {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
}

export const scrollToBottom = (elmentRef: RefObject<HTMLDivElement>, heightToScroll?: number) => {
  if (elmentRef.current) {
    elmentRef.current!.scrollTo(0, heightToScroll ?? elmentRef.current!.scrollHeight);
  }
};

export const isEqual = <T>(obj: T, obj2: T) => {
  return loadsh_isEqual(obj, obj2);
};

export const removeDuplicatesFromOldArrById = <T extends { id: string }>(old: T[], newArr: T[]) => {
  return old.filter((old) => !newArr.find((newItem) => newItem.id === old.id));
};

export const languageMap: Record<string, string> = {
  en: "English",
  ar: "Arabic",
  fr: "French",
  de: "German",
  es: "Spanish",
  it: "Italian",
  pt: "Portuguese",
  ru: "Russian",
  zh: "Chinese",
  ja: "Japanese",
  ko: "Korean",
  nl: "Dutch",
  tr: "Turkish",
  hi: "Hindi",
  sv: "Swedish",
  no: "Norwegian",
  da: "Danish",
  fi: "Finnish",
  pl: "Polish",
  th: "Thai",
  he: "Hebrew",
  id: "Indonesian",
  ms: "Malay",
  cs: "Czech",
  ro: "Romanian",
  hu: "Hungarian",
  vi: "Vietnamese",
  bg: "Bulgarian",
  uk: "Ukrainian",
  el: "Greek",
  // Additional languages
  mt: "Maltese",
  ga: "Irish",
  cy: "Welsh",
  eu: "Basque",
  ca: "Catalan",
  gl: "Galician",
  is: "Icelandic",
  fo: "Faroese",
  sq: "Albanian",
  mk: "Macedonian",
  sr: "Serbian",
  bs: "Bosnian",
  me: "Montenegrin",
  fa: "Persian",
  ur: "Urdu",
  bn: "Bengali",
  ta: "Tamil",
  te: "Telugu",
  ml: "Malayalam",
  kn: "Kannada",
  gu: "Gujarati",
  pa: "Punjabi",
  or: "Odia",
  as: "Assamese",
  ne: "Nepali",
  si: "Sinhala",
  my: "Burmese",
  km: "Khmer",
  lo: "Lao",
  tl: "Tagalog",
  sw: "Swahili",
  am: "Amharic",
  ha: "Hausa",
  yo: "Yoruba",
  ig: "Igbo",
  zu: "Zulu",
  af: "Afrikaans",
  xh: "Xhosa",
  st: "Southern Sotho",
  tn: "Tswana",
  ts: "Tsonga",
  ss: "Swati",
  ve: "Venda",
  nr: "Southern Ndebele",
  sn: "Shona",
  rw: "Kinyarwanda",
  lg: "Ganda",
  ak: "Akan",
  tw: "Twi",
  ee: "Ewe",
  fon: "Fon",
  ff: "Fula",
  wo: "Wolof",
  so: "Somali",
  om: "Oromo",
  ti: "Tigrinya",
  ps: "Pashto",
  sd: "Sindhi",
  ku: "Kurdish",
  az: "Azerbaijani",
  tk: "Turkmen",
  uz: "Uzbek",
  ky: "Kyrgyz",
  kk: "Kazakh",
  mn: "Mongolian",
  bo: "Tibetan",
  dz: "Dzongkha",
  ka: "Georgian",
  hy: "Armenian",
  ab: "Abkhaz",
  os: "Ossetian",
  ce: "Chechen",
  av: "Avar",
  lez: "Lezgin",
  tab: "Tabasaran",
  agx: "Aghul",
  rut: "Rutul",
  tsakh: "Tsakhur",
  kum: "Kumyk",
  nog: "Nogai",
  kar: "Karachay-Balkar",
  bua: "Buryat",
  xal: "Kalmyk",
  tyv: "Tuvan",
  alt: "Southern Altai",
  khk: "Khakas",
  cjs: "Shor",
  ykg: "Yukaghir",
  ckt: "Chukchi",
  kpy: "Koryak",
  ale: "Aleut",
  yup: "Yupik",
  iu: "Inuktitut",
  kl: "Greenlandic",
  cr: "Cree",
  oj: "Ojibwe",
  dak: "Dakota",
  lkt: "Lakota",
  chr: "Cherokee",
  mus: "Creek",
  cho: "Choctaw",
  chk: "Chickasaw",
  seminole: "Seminole",
  mik: "Mikasuki",
  apk: "Apache",
  nav: "Navajo",
  dgo: "Dogrib",
  chp: "Chipewyan",
  gwi: "Gwich'in",
  koy: "Koyukon",
  deg: "Deg Xinag",
  hol: "Holikachuk",
};

export const languageTuples = [
  ["en", "English"],
  ["ar", "Arabic"],
  ["fr", "French"],
  ["de", "German"],
  ["es", "Spanish"],
  ["it", "Italian"],
  ["pt", "Portuguese"],
  ["ru", "Russian"],
  ["zh", "Chinese"],
  ["ja", "Japanese"],
  ["ko", "Korean"],
  ["nl", "Dutch"],
  ["tr", "Turkish"],
  ["hi", "Hindi"],
  ["sv", "Swedish"],
  ["no", "Norwegian"],
  ["da", "Danish"],
  ["fi", "Finnish"],
  ["pl", "Polish"],
  ["th", "Thai"],
  ["he", "Hebrew"],
  ["id", "Indonesian"],
  ["ms", "Malay"],
  ["cs", "Czech"],
  ["ro", "Romanian"],
  ["hu", "Hungarian"],
  ["vi", "Vietnamese"],
  ["bg", "Bulgarian"],
  ["uk", "Ukrainian"],
  ["el", "Greek"],
];

export const getLangNameByLangCode = (langCode: string | undefined | null): string => {
  if (!langCode) {
    return "Unknown";
  }
  return languageMap[langCode.toLowerCase()] || langCode;
};

export const formatScreamingSnakeCase = (value: string): string => {
  return value.replace(/_/g, " ").toLowerCase();
};

export const mergeFilterCleanly = <T extends object>(current: Partial<T>, incoming: Partial<T>): Partial<T> => {
  const updated: Partial<T> = { ...current };

  for (const key in incoming) {
    const value = incoming[key as keyof T];

    if (!value || value === "ALL") {
      delete updated[key as keyof T];
    } else {
      updated[key as keyof T] = value;
    }
  }

  return updated;
};

export function cleanObject<T extends object>(obj: T): Partial<T> {
  return Object.fromEntries(
    Object.entries(obj).filter(([, value]) => value !== undefined && value !== null && value !== "")
  ) as Partial<T>;
}

export const formatCurrency = (amount: number, currency: CurrencyCode) => {
  return currency === "KARMA"
    ? `${amount} ${currency}`
    : new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency || "EUR",
      }).format(amount);
};

export const isEmail = (value: string): boolean => value.includes("@");

export const isPhoneNumber = (value: string): boolean => /^(\+?[1-9]\d{7,14})$/.test(value);

const trimTrailingSlashes = (value: string) => value.replace(/\/+$/, "");

export const getVoucherBaseUrl = (): string => {
  const envBaseUrl = process.env.REACT_APP_VOUCHER_BASE_URL?.trim();
  if (envBaseUrl) {
    return trimTrailingSlashes(envBaseUrl);
  }

  const projectId = (process.env.REACT_APP_PROJECT_ID || "").toLowerCase();

  if (projectId.includes("dev")) {
    return "https://taaly-deeplinks-dev.web.app";
  }

  if (projectId.includes("test")) {
    return "https://taaly-deeplinks-test.web.app";
  }

  return "https://links.taaly.nl";
};

export const generateVoucherDeeplinkUrl = (code: string): string => {
  return `${getVoucherBaseUrl()}/vouchers/${encodeURIComponent(code)}`;
};
